/**
 * Landing Page Entry Point
 *
 * Imports mountTool from the clip looper tool and initializes
 * page-level interactivity (scroll, accordion, form, toast).
 */

import { mountTool } from './main.js';

// GA4 dataLayer queue (captures events before gtag.js loads)
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);};

// Store tool handle for cleanup
let toolHandle = null;

/* ---------- API base URL ---------- */

function getApiBase() {
  return window.location.hostname === 'thecliplooper.com'
    ? 'https://api.thecliplooper.com'
    : '';
}

/* ---------- UTM helpers ---------- */

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  return utm;
}

/* ---------- Email form (T026) ---------- */

export function initSignupForm() {
  const form = document.getElementById('lp-signup-form');
  const emailInput = document.getElementById('lp-email');
  const consentCheckbox = document.getElementById('lp-consent');
  const submitBtn = document.getElementById('lp-submit');
  const messageDiv = document.getElementById('lp-form-message');
  if (!form || !emailInput || !consentCheckbox || !submitBtn) return;

  function updateSubmitState() {
    const validEmail = emailInput.validity.valid && emailInput.value.trim() !== '';
    submitBtn.disabled = !(validEmail && consentCheckbox.checked);
  }

  emailInput.addEventListener('input', updateSubmitState);
  consentCheckbox.addEventListener('change', updateSubmitState);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Belt-and-suspenders honeypot check
    const honeypot = form.querySelector('input[name="website"]');
    if (honeypot && honeypot.value) return;

    const payload = {
      email: emailInput.value.trim(),
      consent_text: consentCheckbox.labels[0]?.textContent?.trim() || 'Consent given',
      source: 'pricing-section',
      ...getUtmParams(),
    };

    submitBtn.disabled = true;
    messageDiv.textContent = '';
    messageDiv.className = 'lp-form-message';

    try {
      const res = await fetch(`${getApiBase()}/api/signups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 202) {
        gtag('event', 'waitlist_signup_submitted', { source: 'pricing-section' });
        messageDiv.textContent = 'Check your email to confirm your signup.';
        messageDiv.classList.add('lp-form-message--success');
        form.querySelector('.lp-signup-form-fields').style.display = 'none';
        form.querySelector('.lp-signup-form-consent').style.display = 'none';
      } else {
        const data = await res.json().catch(() => ({}));
        messageDiv.textContent = data.detail || 'Something went wrong. Please try again.';
        messageDiv.classList.add('lp-form-message--error');
        submitBtn.disabled = false;
      }
    } catch {
      messageDiv.textContent = 'Network error. Please check your connection and try again.';
      messageDiv.classList.add('lp-form-message--error');
      submitBtn.disabled = false;
    }
  });
}

/* ---------- Confirmed toast (T027) ---------- */

export function initConfirmedToast() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('confirmed') !== 'true') return;

  gtag('event', 'waitlist_signup_confirmed');

  const toast = document.createElement('div');
  toast.className = 'lp-toast';
  toast.setAttribute('role', 'status');
  toast.textContent = "You're on the list! We'll notify you when paid tiers launch.";
  document.body.prepend(toast);

  // Remove query param to prevent showing on refresh
  params.delete('confirmed');
  const clean = params.toString();
  const url = window.location.pathname + (clean ? '?' + clean : '');
  history.replaceState(null, '', url);

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    toast.classList.add('lp-toast--fade');
    toast.addEventListener('transitionend', () => toast.remove());
  }, 5000);
}

/* ---------- Pricing section tracking ---------- */

export function initPricingSectionTracking() {
  const pricing = document.getElementById('pricing');
  if (!pricing) return;

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        gtag('event', 'pricing_section_viewed');
        observer.disconnect();
      }
    },
    { threshold: 0.3 }
  );
  observer.observe(pricing);
}

/* ---------- Smooth scroll (T031) ---------- */

export function initSmoothScroll() {
  const header = document.querySelector('.lp-header');
  if (!header) return;

  header.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const targetId = link.getAttribute('href');
    const target = document.querySelector(targetId);
    if (!target) return;

    e.preventDefault();

    const headerHeight = header.offsetHeight;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
  });
}

/* ---------- FAQ accordion (T030) ---------- */

export function initFaqAccordion() {
  const buttons = document.querySelectorAll('.lp-faq-question');
  if (!buttons.length) return;

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') === 'true';
      const answerId = button.getAttribute('aria-controls');
      const answer = document.getElementById(answerId);
      if (!answer) return;

      button.setAttribute('aria-expanded', String(!expanded));

      if (expanded) {
        answer.hidden = true;
      } else {
        answer.hidden = false;
      }
    });
  });
}

/* ---------- Hamburger menu (T033) ---------- */

export function initHamburger() {
  const btn = document.querySelector('.lp-hamburger');
  const nav = document.querySelector('.lp-header-nav');
  if (!btn || !nav) return;

  btn.addEventListener('click', () => {
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    nav.classList.toggle('lp-header-nav--open');
  });

  // Close menu when a nav link is clicked
  nav.addEventListener('click', (e) => {
    if (e.target.closest('a')) {
      btn.setAttribute('aria-expanded', 'false');
      nav.classList.remove('lp-header-nav--open');
    }
  });
}

/* ---------- Init ---------- */

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('tool-container');
  if (container) {
    toolHandle = mountTool(container);
  }

  initHamburger();
  initSmoothScroll();
  initFaqAccordion();
  initSignupForm();
  initConfirmedToast();
  initPricingSectionTracking();
});
